import { FC, useEffect, useMemo, useState } from "react";
import classes from './changeDayInCalendar.module.scss'
import { calendarService, ICalendarDay, ICalendarFilter } from "../../../entities/calendar";
import { ITrialCalendarDay, ITrialCalendarFilter, trialLessonService } from "../../../entities/trialLesson";
import { IShiftCalendarDay, shiftService, TShiftStatus } from "../../../entities/shift";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { useGetMonthWeeks } from "../../../shared/lib/hooks/useGetMonthWeeks";

interface IShiftCalendarFilter {
    tgAccountIds?: string[];
}

interface IProps {
    date: Date;
    filter?: ICalendarFilter;
    trialFilter?: ITrialCalendarFilter;
    trialCreationFilter?: ITrialCalendarFilter;
    shiftFilter?: IShiftCalendarFilter;
    onSelectDate?: (date: Date) => void;
}

const isSameDay = (firstDate: Date, secondDate: Date): boolean => {
    return firstDate.getFullYear() === secondDate.getFullYear() &&
        firstDate.getMonth() === secondDate.getMonth() &&
        firstDate.getDate() === secondDate.getDate()
}

const isSameMonth = (firstDate: Date, secondDate: Date): boolean => {
    return firstDate.getFullYear() === secondDate.getFullYear() &&
        firstDate.getMonth() === secondDate.getMonth()
}

const getLocalDateFromDate = (date: Date) => ({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
})

const getShiftStatusLabel = (status: TShiftStatus): string => {
    switch(status){
        case 'SHIFT_STATUS_PLANNED':
            return 'запланировано'
        case 'SHIFT_STATUS_COMPLETED':
            return 'завершено'
        case 'SHIFT_STATUS_CANCELLED':
            return 'отменено'
        default:
            return 'нет смены'
    }
}

export const ChangeDayInCalendar: FC<IProps> = ({
    date, filter, trialFilter, trialCreationFilter, shiftFilter, onSelectDate,
}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [visibleMonth, setVisibleMonth] = useState<Date>(
        new Date(date.getFullYear(), date.getMonth(), 1)
    )

    const [calendarDays, setCalendarDays] = useState<ICalendarDay[]>([])
    const [trialCalendarDays, setTrialCalendarDays] = useState<ITrialCalendarDay[]>([])
    const [shiftCalendarDays, setShiftCalendarDays] = useState<IShiftCalendarDay[]>([])

    const weeks = useGetMonthWeeks(visibleMonth)
    const monthTitle = visibleMonth
        .toLocaleString('ru-RU', {month: 'long', year: 'numeric'})
        .replace(/\s*г\.$/, '')
    const hasShiftFilter = Boolean(shiftFilter)
    const shiftFilterKey = shiftFilter?.tgAccountIds?.join(',') ?? ''


    const calendarDaysMap = useMemo(() => {
        return new Map(
            calendarDays.map(day => [
                `${day.date.year}-${day.date.month}-${day.date.day}`,
                day,
            ])
        )
    }, [calendarDays])

    const getCalendarDayInfo = (dayDate: Date) => {
        return calendarDaysMap.get(
            `${dayDate.getFullYear()}-${dayDate.getMonth() + 1}-${dayDate.getDate()}`
        )
    }

    const trialCalendarDaysMap = useMemo(() => {
        return new Map(
            trialCalendarDays.map(day => [
                `${day.date.year}-${day.date.month}-${day.date.day}`,
                day,
            ])
        )
    }, [trialCalendarDays])

    const getTrialCalendarDayInfo = (dayDate: Date) => {
        return trialCalendarDaysMap.get(
            `${dayDate.getFullYear()}-${dayDate.getMonth() + 1}-${dayDate.getDate()}`
        )
    }

    const shiftCalendarDaysMap = useMemo(() => {
        return new Map(
            shiftCalendarDays.map(day => [
                `${day.date.year}-${day.date.month}-${day.date.day}`,
                day,
            ])
        )
    }, [shiftCalendarDays])

    const getShiftCalendarDayInfo = (dayDate: Date) => {
        return shiftCalendarDaysMap.get(
            `${dayDate.getFullYear()}-${dayDate.getMonth() + 1}-${dayDate.getDate()}`
        )
    }

    const getDayMarkers = (dayDate: Date) => {
        if(trialFilter){
            const trialCalendarDay = getTrialCalendarDayInfo(dayDate)

            if(!trialCalendarDay) return null

            return (
                <>
                    {trialCalendarDay.assignedCount > 0 && (
                        <span className={`${classes.marker} ${classes.trialAssignedMarker}`} aria-hidden="true" />
                    )}
                    {trialCalendarDay.lostCount > 0 && (
                        <span className={`${classes.marker} ${classes.trialLostMarker}`} aria-hidden="true" />
                    )}
                    {trialCalendarDay.orangeCount > 0 && (
                        <span className={`${classes.marker} ${classes.trialOrangeMarker}`} aria-hidden="true" />
                    )}
                    {trialCalendarDay.completedCount > 0 && (
                        <span className={`${classes.marker} ${classes.trialCompletedMarker}`} aria-hidden="true" />
                    )}
                    {trialCalendarDay.studentCreatedCount > 0 && (
                        <span className={`${classes.marker} ${classes.trialStudentCreatedMarker}`} aria-hidden="true" />
                    )}
                </>
            )
        }

        if(shiftFilter){
            const shiftCalendarDay = getShiftCalendarDayInfo(dayDate)

            if(!shiftCalendarDay) return null

            return (
                <>
                    {shiftCalendarDay.status === 'SHIFT_STATUS_COMPLETED' && (
                        <span className={`${classes.marker} ${classes.shiftCompletedMarker}`} aria-hidden="true" />
                    )}
                    {shiftCalendarDay.status === 'SHIFT_STATUS_CANCELLED' && (
                        <span className={`${classes.marker} ${classes.shiftCancelledMarker}`} aria-hidden="true" />
                    )}
                    {shiftCalendarDay.hasReplacements && (
                        <span className={`${classes.marker} ${classes.shiftReplacementMarker}`} aria-hidden="true" />
                    )}
                    {shiftCalendarDay.assignedTeamCount > 0 && (
                        <span className={`${classes.marker} ${classes.shiftAssignedMarker}`} aria-hidden="true" />
                    )}
                </>
            )
        }

        const calendarDay = getCalendarDayInfo(dayDate)

        if(!calendarDay) return null

        return (
            <>
                {calendarDay.scheduledCount > 0 && (
                    <span className={`${classes.marker} ${classes.scheduledMarker}`} aria-hidden="true" />
                )}
                {calendarDay.cancelledCount > 0 && (
                    <span className={`${classes.marker} ${classes.cancelledMarker}`} aria-hidden="true" />
                )}
                {calendarDay.rescheduledCount > 0 && (
                    <span className={`${classes.marker} ${classes.rescheduledMarker}`} aria-hidden="true" />
                )}
                {calendarDay.completedCount > 0 && (
                    <span className={`${classes.marker} ${classes.completedMarker}`} aria-hidden="true" />
                )}
            </>
        )
    }

    const getDayTitle = (dayDate: Date): string | undefined => {
        if(trialFilter){
            const trialCalendarDay = getTrialCalendarDayInfo(dayDate)

            if(!trialCalendarDay) return undefined

            const items = [
                `назначено: ${trialCalendarDay.assignedCount}`,
                `слито: ${trialCalendarDay.lostCount}`,
                `проведено: ${trialCalendarDay.orangeCount}`,
                `студент создан: ${trialCalendarDay.studentCreatedCount}`,
            ].filter(item => !item.endsWith(': 0'))

            return items.length ? items.join(', ') : undefined
        }

        if(shiftFilter){
            const shiftCalendarDay = getShiftCalendarDayInfo(dayDate)

            if(!shiftCalendarDay) return undefined

            const items = [
                `статус: ${getShiftStatusLabel(shiftCalendarDay.status)}`,
                `звеньев назначено: ${shiftCalendarDay.assignedTeamCount}`,
                `подмены: ${shiftCalendarDay.hasReplacements ? 'да' : 'нет'}`,
            ].filter(item => !item.endsWith(': 0') && !item.endsWith(': нет'))

            return items.length ? items.join(', ') : undefined
        }

        const calendarDay = getCalendarDayInfo(dayDate)

        if(!calendarDay) return undefined

        const items = [
            `запланировано: ${calendarDay.scheduledCount}`,
            `проведено: ${calendarDay.completedCount}`,
            `отменено: ${calendarDay.cancelledCount}`,
            `перенесено: ${calendarDay.rescheduledCount}`,
        ].filter(item => !item.endsWith(': 0'))

        return items.length ? items.join(', ') : undefined
    }

    useEffect(() => {
        setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1))
    }, [date])


    useEffect(() => {
        if(!filter && !trialFilter && !shiftFilter){
            setCalendarDays([])
            setTrialCalendarDays([])
            setShiftCalendarDays([])
            return
        }

        let isActual = true
        setCalendarDays([])
        setTrialCalendarDays([])
        setShiftCalendarDays([])

        const getData = async () => {
            try{
                if(shiftFilter){
                    const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1)
                    const lastDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0)
                    const response = await shiftService.getShiftCalendar({
                        from: getLocalDateFromDate(firstDay),
                        to: getLocalDateFromDate(lastDay),
                        tgAccountIds: shiftFilter.tgAccountIds,
                    })

                    if(isActual) setShiftCalendarDays(response.days)
                    return
                }

                if(trialFilter){
                    const response = await trialLessonService.getTrialMonthCalendar({
                        year: visibleMonth.getFullYear(),
                        month: visibleMonth.getMonth() + 1,
                        filter: trialFilter,
                        creationFilter: trialCreationFilter,
                    })

                    if(isActual) setTrialCalendarDays(response.days)
                    return
                }

                if(filter){
                    const response = await calendarService.getMonthCalendar({
                        year: visibleMonth.getFullYear(),
                        month: visibleMonth.getMonth() + 1,
                        filter,
                    })

                    if(isActual) setCalendarDays(response.days)
                }
            }
            catch(error){
                console.log(error)

                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: shiftFilter
                        ? 'Ошибка при получении календаря смен'
                        : trialFilter
                            ? 'Ошибка при получении календаря пробных занятий'
                            : 'Ошибка при получении календаря', type: 'error'})
                }
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [filter, hasShiftFilter, setGlobalMessage, setIsAuth, shiftFilter, shiftFilterKey, trialCreationFilter, trialFilter, visibleMonth])

    const changeVisibleMonth = (monthOffset: number) => {
        setVisibleMonth(currentMonth => (
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1)
        ))
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <h3 className={classes.title}>{monthTitle}</h3>
                <nav className={classes.navigation} aria-label="Переключение месяца">
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => changeVisibleMonth(-1)}
                        aria-label="Предыдущий месяц"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => changeVisibleMonth(1)}
                        aria-label="Следующий месяц"
                    >
                        ›
                    </button>
                </nav>
            </section>
            <section className={classes.calendarFrame}>
                <table className={classes.month}>
                    <thead>
                        <tr>
                            <th scope="col">Пн</th>
                            <th scope="col">Вт</th>
                            <th scope="col">Ср</th>
                            <th scope="col">Чт</th>
                            <th scope="col">Пт</th>
                            <th scope="col">Сб</th>
                            <th scope="col">Вс</th>
                        </tr>
                    </thead>
                    <tbody>
                        {weeks.map((week, weekIndex) => (
                            <tr key={weekIndex}>
                                {week.map((day) => {
                                    const isSelected = isSameDay(day.date, date)
                                    const isOtherMonth = !isSameMonth(day.date, visibleMonth)
                                    const today = new Date()
                                    const isPast = day.date < new Date(
                                        today.getFullYear(), today.getMonth(), today.getDate(),
                                    )
                                    const trialDay = getTrialCalendarDayInfo(day.date)
                                    const isUnavailable = trialFilter
                                        ? Boolean(trialCreationFilter) && !isPast &&
                                            trialDay?.availabilityStatus === 'TRIAL_AVAILABILITY_STATUS_UNAVAILABLE'
                                        : getCalendarDayInfo(day.date)?.isAvailable === false

                                    const dayTitle = getDayTitle(day.date)

                                    return (
                                        <td
                                            key={day.date.getTime()}
                                            className={classes.day +
                                                (day.class === 'current' ? ` ${classes.current}` : '') +
                                                (isOtherMonth ? ` ${classes.opacity}` : '') +
                                                (isSelected ? ` ${classes.selected}` : '') +
                                                (isUnavailable ? ` ${classes.unavailable}` : '')
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={classes.dayButton}
                                                onClick={() => onSelectDate?.(day.date)}
                                                aria-current={isSelected ? 'date' : undefined}
                                                title={isUnavailable
                                                    ? [dayTitle, trialFilter
                                                        ? 'Нет свободного времени для пробного занятия'
                                                        : 'Недоступен для назначения'
                                                    ].filter(Boolean).join(' · ')
                                                    : dayTitle
                                                }
                                            >
                                                {day.date.getDate()}
                                            </button>
                                            {getDayMarkers(day.date)}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </section>
    )
}
